import React, { useEffect, useMemo } from 'react';
import moment from 'moment';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useLocation } from 'react-router-dom';

import EventContent from './Components/EventContent.jsx';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { useCalendarContext } from 'modules/calendar/Context/CalendarContext';
import { getLocalStorageParseItem, removeLocalStorageItem } from 'utils/localStorageUtils';
import { handleRedirection, isOutsideBusinessHours } from 'utils/utils';

function CalendarView() {
  const {
    eventList,
    outlookEventList,
    calendarSettings,
    setShowEventPreviewModal,
    setSelectedEvent,
    currentDates,
    setCurrentDates,
    setCurrentView,
    currentView,
    calenderDayClick,
  } = useCalendarContext();
  const userContext = useUserDetailsContext();

  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const queryEventDate = queryParams.get('date');

  // Fallback to undefined if not provided
  const initialDate = useMemo(() => {
    return queryEventDate || undefined;
  }, [queryEventDate]);

  let timezone = userContext?.userDetails?.timezone;

  const onEventClick = (info) => {
    let eventSelected;
    if (info?.event !== undefined) {
      eventSelected = info.event.toJSON();
    } else {
      eventSelected = {
        extendedProps: info,
        title: info?.title,
        start: info?.meeting_start_time ? info?.meeting_start_time : info?.start,
        end: info?.meeting_end_time ? info?.meeting_end_time : info?.end,
      };
    }
    setSelectedEvent(eventSelected);
    if (eventSelected.extendedProps.res_type === 'user') {
      const arr = eventSelected?.extendedProps?.sk?.split('#');
      const contactId = arr && arr.length > 0 && arr[arr.length - 1];
      const url = contactId ? `/contacts/${contactId}` : '/contacts';
      handleRedirection(history, url);
    } else {
      setShowEventPreviewModal(true);
    }
  };

  const options = {
    plugins: [multiMonthPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin],

    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next,today',
      center: 'title',
      right: 'multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay',
    },
    allDayText: 'All Day',
    editable: false,
    showNonCurrentDates: true,
    dayPopoverFormat: { month: '2-digit', day: '2-digit', year: 'numeric' },
    events: function (_info, successCallback) {
      let event_list = Array.isArray(eventList)
        ? eventList.map((event) => ({
            ...event,
            allDay: event?.all_day === true,
          }))
        : [];

      //Filter events based on timezone and show business time events from 9am to 5pm
      event_list = event_list?.filter((event) => {
        if (event?.res_type === 'event' && calendarSettings?.show_business_times_only) {
          return !isOutsideBusinessHours(event, timezone);
        } else return true;
      });

      let outlook_event_list = Array.isArray(outlookEventList) ? outlookEventList : [];

      const concatenatedArray = [...event_list, ...outlook_event_list];

      successCallback(concatenatedArray);
    },

    eventContent: EventContent,
    eventClick: onEventClick,

    weekends: calendarSettings?.show_weekends,
    slotMinTime: calendarSettings?.show_business_times_only ? '09:00' : '00:00:00',
    slotMaxTime: calendarSettings?.show_business_times_only ? '17:00' : '24:00:00',
    hiddenDays: calendarSettings?.show_weekends ? [] : [0, 6],
    views: {
      timelineCustom: {
        type: 'timeGrid',
        dateIncrement: { years: 1 },
        slotDuration: { months: 1 },
        visibleRange: function (currentDate) {
          let d = new Date();
          return {
            start: `${d.getFullYear()}-01-01 `,
            end: `${d.getFullYear()}-12-31`,
          };
        },
      },
    },
    slotLaneClassNames: (arg) => {
      return arg.view.type === 'timeGridDay' ? ['cursor-pointer'] : [];
    },
  };

  const handleViewDidMount = (data) => {
    setCurrentView(data.view?.type);
  };

  const handleDatesSet = (dateInfo) => {
    let current_start = dateInfo?.start;
    let current_end = dateInfo?.end;

    // Update current view when dates are set (this happens when view changes)
    if (dateInfo?.view?.type && dateInfo.view.type !== currentView) {
      setCurrentView(dateInfo.view.type);
    }

    //to avoid infinite recursion
    if (current_start.valueOf() !== currentDates.currentStartValue || current_end.valueOf() !== currentDates.currentEndValue) {
      setCurrentDates({
        currentStartValue: current_start.valueOf(),
        currentEndValue: current_end.valueOf(),
        currentStartDate: current_start,
        currentEndDate: current_end,
      });
    }
  };

  const generateFormattedEventDetails = (data = {}) => {
    const { title, meeting_start_time, meeting_end_time, sk, pk, icon = '' } = data;

    let formattedEventDetails = {
      allDay: false,
      title: title,
      extendedProps: {
        meeting_start_time,
        meeting_end_time,
        res_type: 'event',
        icon,
        sk,
        pk,
      },
    };
    return formattedEventDetails;
  };

  const handleDateClick = (arg) => {
    if (arg?.jsEvent?.target?.classList?.contains('fc-daygrid-day-number')) {
      const calendarApi = arg.view.calendar;
      calendarApi.changeView('timeGridDay', arg.date);
    }
    if (arg.view.type === 'timeGridDay') {
      calenderDayClick(arg, true);
    }
  };

  useEffect(() => {
    let selectedEventDetails = getLocalStorageParseItem('stateData')?.viewEvent || location?.state?.viewEvent;
    if (selectedEventDetails) {
      setSelectedEvent(generateFormattedEventDetails(selectedEventDetails));
      setShowEventPreviewModal(true);
      removeLocalStorageItem('stateData');
    }
  }, [window?.localStorage, location]);

  return (
    <div className="row mx-0">
      <div className="col-12 px-0">
        <div className="Calendar-wrap shadow-sm mb-3">
          <FullCalendar
            {...options}
            dayMaxEvents={2}
            eventBackgroundColor="white"
            eventBorderColor="white"
            eventOverlap={false}
            slotEventOverlap={false}
            viewDidMount={handleViewDidMount}
            datesSet={handleDatesSet}
            lazyFetching={true}
            multiMonthMaxColumns={3}
            dateClick={handleDateClick}
            height={'auto'}
            timeZone={timezone || 'local'}
            initialDate={initialDate}
          />
        </div>
      </div>
    </div>
  );
}

export default CalendarView;
